import AuthUtil from '../../redux/AuthUtil';
import { BONUS_API } from '../global/Server/endpoints';
import { summarizePendingPhotos } from './photoOutbox';

// Record photos this device cannot produce, so they stop blocking the shift.
//
// If the bytes are not in this device's outbox there is no way to ever send
// them — the photo is missing, not late. Left counted, it would hold the
// employee at the clock-out gate indefinitely over something nobody can
// supply. Marking it lost does not hide it: the bonus keeps no photo, carries
// photoLostAt and a reason.
//
// Done at the gate rather than in the background, and only after the outbox
// has been given a chance to upload, so this can never write off a photo the
// device was about to send.
export async function writeOffUnproducible(bonusIds, reason) {
  if (!bonusIds || !bonusIds.length) return 0;
  await Promise.all(
    bonusIds.map((bonusId) =>
      AuthUtil.postRequest(BONUS_API.REPORT_PHOTO_FAILURE, {
        bonusId,
        part: 'crop',
        permanent: true,
        reason:
          reason || 'device is not holding this photo — nothing left to upload',
        attempts: 0,
      }).catch(() => null)
    )
  );
  return bonusIds.length;
}

// What is still missing, from both places that know.
//
// The two halves of this live on different devices. A bonus photo is taken on
// a phone and waits in THAT phone's IndexedDB outbox; clocking out happens on
// the shop computer, which has no way to see it. So a gate that only read the
// local outbox would correctly block on the phone and silently never block on
// the computer — which is the screen the employee actually clocks out from.
//
// The server is the only party that sees both, so it is the authority here:
// it reports bonuses in the open shift whose photo has not arrived, whatever
// device is holding it. The local outbox is still merged in because it knows
// things the server cannot — whether this device is mid-retry, and whether
// attempts have stalled — and because it still answers when the network is
// down, which is exactly when photos are stuck.
export async function pendingPhotoStatus(user) {
  const local = await summarizePendingPhotos().catch(() => ({
    count: 0,
    bonusIds: [],
    stalled: 0,
  }));

  let server = { count: 0, bonusIds: [], reachable: false };
  try {
    const res = await AuthUtil.postRequest(BONUS_API.PENDING_PHOTOS, {
      userId: user?.userId,
      locationId: user?.selectedLocationId,
    });
    if (res?.data?.success) {
      server = {
        count: res.data.count || 0,
        bonusIds: res.data.bonusIds || [],
        reachable: true,
      };
    }
  } catch (error) {
    // Offline or the server is down — fall back to what this device knows.
    // Under-reporting here is the safe direction: the gate still blocks on
    // anything held locally, and the server check runs again on the next open.
  }

  const bonusIds = Array.from(
    new Set([...(local.bonusIds || []), ...(server.bonusIds || [])])
  );

  // Photos the server is still waiting on that this device is not holding.
  //
  // There is nothing left to upload for these: the bytes are not here, so no
  // amount of retrying or waiting produces them. Only meaningful when the
  // server actually answered — if it did not, "not in the local outbox" tells
  // us nothing.
  const localIds = new Set(local.bonusIds || []);
  const unproducible = server.reachable
    ? (server.bonusIds || []).filter((id) => !localIds.has(id))
    : [];

  return {
    count: bonusIds.length,
    bonusIds,
    unproducible,
    // Stalled is a device-local notion: only the phone holding the photo knows
    // its attempts keep failing. On the computer this stays 0, so the gate
    // there asks the employee to go and deal with it on the phone rather than
    // offering an escape hatch for a photo it cannot even see.
    stalled: local.stalled || 0,
    onThisDevice: local.count || 0,
    serverReachable: server.reachable,
  };
}
